import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { logSecurityEvent } from '@/utils/securityUtils';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import { sanitizeRedirectPath } from '@/utils/redirectUtils';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any, data: any }>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Session timeout for security
  const sessionTimeoutMinutes = isAdmin ? 15 : 30; // Shorter timeout for admins

  // Function to check if user is admin using secure database check
  const checkIsAdmin = async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('check_admin_status');
      if (error) {
        console.warn('Error checking admin status:', error);
        return false;
      }
      return data || false;
    } catch (error) {
      console.warn('Failed to check admin status:', error);
      return false;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed:", event);
        setSession(session);
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        
        // Check if the user is admin using secure database function
        if (currentUser) {
          // Use setTimeout to avoid blocking the auth state change
          setTimeout(async () => {
            try {
              const userIsAdmin = await checkIsAdmin();
              setIsAdmin(userIsAdmin);
              console.log("Current user email:", currentUser?.email);
              console.log("Is admin?", userIsAdmin);
            } catch (error) {
              console.warn("Failed to check admin status:", error);
              setIsAdmin(false);
            }
          }, 0);
        } else {
          setIsAdmin(false);
          console.log("No user, admin status: false");
        }
        
        if (event === 'SIGNED_IN') {
          toast.success('Successfully signed in!');
          // Note: Admin status check is now async, so navigation logic moved to after admin check
          setTimeout(async () => {
            try {
              const userIsAdmin = await checkIsAdmin();
              if (userIsAdmin) {
                console.log("ADMIN USER DETECTED - Enabling admin functionality");
                // Force navigation to subscriptions page for admin users
                if (window.location.pathname === '/auth') {
                  window.location.href = '/subscriptions';
                }
              } else {
                // Handle regular user navigation
                if (window.location.pathname === '/auth') {
                  const redirectPath = localStorage.getItem('postAuthRedirect') || 
                                      sessionStorage.getItem('postAuthRedirect');
                  localStorage.removeItem('postAuthRedirect');
                  sessionStorage.removeItem('postAuthRedirect');
                  const safePath = sanitizeRedirectPath(redirectPath);
                  window.location.href = safePath;
                }
              }
            } catch (error) {
              console.warn("Failed to check admin status for navigation:", error);
              // Default to regular user navigation
              if (window.location.pathname === '/auth') {
                const redirectPath = localStorage.getItem('postAuthRedirect') || 
                                sessionStorage.getItem('postAuthRedirect');
                localStorage.removeItem('postAuthRedirect');
                sessionStorage.removeItem('postAuthRedirect');
                const safePath = sanitizeRedirectPath(redirectPath);
                window.location.href = safePath;
              }
            }
          }, 100); // Small delay to allow admin check
        } else if (event === 'SIGNED_OUT') {
          toast.success('Successfully signed out!');
        } else if (event === 'PASSWORD_RECOVERY') {
          toast.success('Password recovery email sent!');
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('Token refreshed successfully');
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      
      // Check if the user is admin using secure database function
      if (currentUser) {
        // Use setTimeout to avoid blocking the session initialization
        setTimeout(async () => {
          try {
            const userIsAdmin = await checkIsAdmin();
            setIsAdmin(userIsAdmin);
            console.log("Initial session check - user email:", currentUser?.email);
            console.log("Initial session check - is admin?", userIsAdmin);
          } catch (error) {
            console.warn("Failed to check admin status:", error);
            setIsAdmin(false);
          }
        }, 0);
      } else {
        setIsAdmin(false);
        console.log("Initial session check - no user, admin status: false");
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string) => {
    try {
      console.log("Starting signup process for:", email);
      // Always redirect new customers to production, not preview
      const redirectUrl = `https://www.binsandthings.co.uk/auth`;
      console.log("Using redirect URL:", redirectUrl);
      
      // First check if user already exists
      const { data: existingUser } = await supabase.auth.getUser();
      if (existingUser.user?.email === email) {
        return { 
          error: { 
            message: "You are already registered with this email. Please sign in instead or check your email for verification." 
          } 
        };
      }
      
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            confirm_registration: true
          }
        }
      });

      if (error) {
        console.error("Error creating user account:", error);
        console.error("Full error object:", JSON.stringify(error, null, 2));
        console.error("Error message:", error.message);
        console.error("Error code:", error.code);
        
        // Handle specific error cases with clearer messages
        if (error.message?.includes('User already registered') || 
            error.message?.includes('already been registered') ||
            error.message?.includes('already registered') ||
            error.message?.includes('Email rate limit exceeded') ||
            error.code === 'email_address_already_confirmed') {
          return { 
            error: { 
              message: "This email is already registered. Please check your inbox (including spam/junk folder - don't let verification emails get binned!) for the verification email, or try signing in if you've already verified your account." 
            } 
          };
        }
        
        if (error.message?.includes('Password should be at least')) {
          return { 
            error: { 
              message: "Password must be at least 6 characters long. Please choose a stronger password." 
            } 
          };
        }
        
        if (error.message?.includes('Signup requires a valid password')) {
          return { 
            error: { 
              message: "Please enter a valid password (at least 6 characters)." 
            } 
          };
        }
        
        return { 
          error: { 
            message: error.message || "An error occurred during signup. Please try again." 
          } 
        };
      }

      console.log("User signup response:", data);

      // Check if user was created successfully
      if (data.user) {
        // Check if this is a repeated signup (user already exists)
        if (data.user.confirmation_sent_at && !data.session) {
          console.log("Repeated signup detected for existing user:", email);
          return { 
            error: { 
              message: "This email is already registered. Please check your inbox (including spam/junk folder - don't let verification emails get binned!) for the verification email, or try signing in if you've already verified your account." 
            } 
          };
        }
        
        console.log("Signup successful, verification email sent to:", email);
        return { error: null };
      } else {
        return { 
          error: { 
            message: "Signup failed. Please try again or contact support if the problem persists." 
          } 
        };
      }

    } catch (error) {
      console.error("Exception in signup process:", error);
      return { 
        error: { 
          message: "An unexpected error occurred. Please try again." 
        } 
      };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        // Log failed authentication attempt
        await logSecurityEvent({
          event_type: 'auth_failure',
          details: `Failed login attempt for email: ${email}`,
          severity: 'medium'
        });
        return { error };
      }
      
      // Log successful authentication
      await logSecurityEvent({
        event_type: 'api_access',
        details: `Successful login for email: ${email}`,
        severity: 'low'
      });
      
      return { error: null };
    } catch (error) {
      // Log unexpected error
      await logSecurityEvent({
        event_type: 'auth_failure',
        details: `Unexpected error during login for email: ${email}`,
        severity: 'high'
      });
      return { error };
    }
  };

  const signOut = async () => {
    setLoading(true);
    await supabase.auth.signOut();
    setLoading(false);
  };

  const resetPassword = async (email: string) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://www.binsandthings.co.uk/auth?reset=true',
      });
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  };

  return (
    <AuthContext.Provider value={{ session, user, loading, isAdmin, signUp, signIn, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
